import React, { useMemo, useRef, useState } from 'react';
import {
    Box,
    Divider,
    InputAdornment,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import type { CampaignReportingAssignedVia } from 'c-sdk';
import type { DashboardSource } from './useDashboardSources';

/** Anything the picker can list: an assigned config, or one of the viewer's own/built-in ones. */
export type PickerSource = {
    key: string;
    name: string;
    kind: string;
    assignedVia?: CampaignReportingAssignedVia;
};

type Props = {
    sources: PickerSource[];
    activeKey: string | null;
    onSelect: (key: string) => void;
    /** What is being picked, singular and lower case. */
    noun?: string;
    nounPlural?: string;
    /** Secondary line for each non-assigned source, by its kind. */
    kindLabels?: Record<string, string>;
    /** Shown before the arrow, and clickable with it. */
    children?: React.ReactNode;
};

export const VIA_LABELS: Record<string, string> = {
    user: 'Assigned to you',
    buyer: 'Via your team',
    agency: 'Via your agency',
    client: 'Via your brand',
    owner: 'Via your media owner',
};

const DASHBOARD_KIND_LABELS: Record<string, string> = { local: 'Built by you' };

/**
 * The arrow on the Custom tab: shows which dashboard is being viewed and switches between the
 * viewer's own and any assigned to them. The Summary tab reuses it with its own noun and labels.
 */
const DashboardSourcePicker: React.FC<Props> = ({
    sources,
    activeKey,
    onSelect,
    noun = 'dashboard',
    nounPlural = `${noun}s`,
    kindLabels = DASHBOARD_KIND_LABELS,
    children,
}) => {
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return sources;
        return sources.filter(s => s.name.toLowerCase().includes(q));
    }, [sources, query]);

    const close = () => {
        setOpen(false);
        setQuery('');
    };

    return (
        <>
            <Box
                ref={anchorRef}
                component="span"
                role="button"
                aria-label={`Choose ${noun}`}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    ml: children ? 0 : 0.5,
                    cursor: 'pointer',
                }}
                onClick={event => {
                    // The arrow picks a source; it must not also switch tab.
                    event.stopPropagation();
                    event.preventDefault();
                    setOpen(true);
                }}
            >
                {children}
                <ArrowDropDownIcon fontSize="small" />
            </Box>
            <Menu
                open={open}
                anchorEl={anchorRef.current}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { minWidth: 280, maxHeight: 420 } } }}
            >
                <Box sx={{ px: 1.5, pt: 0.5, pb: 1 }}>
                    <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        placeholder={`Search ${nounPlural}…`}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        // Typing in a Menu otherwise jumps focus to whichever item starts
                        // with that letter.
                        onKeyDown={e => e.stopPropagation()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <Divider />

                {filtered.length === 0 && (
                    <MenuItem disabled>
                        <Typography variant="body2">No {nounPlural} match</Typography>
                    </MenuItem>
                )}

                {filtered.map(source => (
                    <MenuItem
                        key={source.key}
                        selected={source.key === activeKey}
                        onClick={() => {
                            onSelect(source.key);
                            close();
                        }}
                    >
                        <ListItemText
                            primary={source.name}
                            secondary={
                                source.kind === 'assigned'
                                    ? VIA_LABELS[source.assignedVia ?? ''] ?? 'Assigned'
                                    : kindLabels[source.kind] ?? ''
                            }
                            secondaryTypographyProps={{ variant: 'caption' }}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export const DashboardTabLabel: React.FC<{
    name: string;
    sources: DashboardSource[];
    activeKey: string | null;
    onSelect: (key: string) => void;
}> = ({ name, sources, activeKey, onSelect }) => (
    <Stack direction="row" alignItems="center" component="span">
        <Box
            component="span"
            sx={{
                maxWidth: 220,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
        >
            {name}
        </Box>
        {sources.length > 1 && (
            <DashboardSourcePicker sources={sources} activeKey={activeKey} onSelect={onSelect} />
        )}
    </Stack>
);

export default DashboardSourcePicker;
